import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useTranslation } from '../../utils/i18n-utils';
import { 
  getEmployees, 
  createEmployee, 
  updateEmployee, 
  deleteEmployee, 
  createEmployeeShift,
  Employee 
} from '../../api/employees';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const Employees: React.FC = () => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [currentEmployee, setCurrentEmployee] = useState<Partial<Employee>>({});
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [openAttendanceDialog, setOpenAttendanceDialog] = useState<boolean>(false);
  const [currentAttendance, setCurrentAttendance] = useState<{
    employeeId: number | null;
    employeeName: string;
    date: Date;
    timeIn: Date | null;
    timeOut: Date | null;
    status: 'present' | 'absent' | 'late' | 'scheduled';
    lateMinutes?: number;
    notes: string;
  }>({
    employeeId: null,
    employeeName: '',
    date: new Date(),
    timeIn: null,
    timeOut: null,
    status: 'present',
    notes: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const data = await getEmployees();
      setEmployees(data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch employees:', err);
      setError('Failed to load employees. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mode: 'add' | 'edit', employee?: Employee) => {
    setDialogMode(mode);
    
    if (mode === 'edit' && employee) {
      setCurrentEmployee(employee);
      setDateOfBirth(employee.dateOfBirth ? new Date(employee.dateOfBirth) : null);
    } else {
      setCurrentEmployee({
        name: '',
        mobileNumber: '',
        rateType: 'daily',
        rateAmount: 0,
        isActive: true
      });
      setDateOfBirth(null);
    }
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentEmployee({
      ...currentEmployee,
      [name]: value
    });
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Convert string booleans to actual booleans
    if (value === "true" || value === "false") {
      processedValue = value === "true";
    }
    
    setCurrentEmployee({
      ...currentEmployee,
      [name]: processedValue
    });
  };

  const handleSubmit = async () => {
    if (!currentEmployee.name || !dateOfBirth || !currentEmployee.mobileNumber || !currentEmployee.rateAmount) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields.',
        severity: 'error'
      });
      return;
    }
    
    try {
      const employeeData = {
        ...currentEmployee,
        dateOfBirth: format(dateOfBirth, 'yyyy-MM-dd')
      };
      
      if (dialogMode === 'add') {
        await createEmployee(employeeData);
        setSnackbar({
          open: true,
          message: 'Employee added successfully!',
          severity: 'success'
        });
      } else {
        if (currentEmployee.id) {
          await updateEmployee(currentEmployee.id, employeeData);
          setSnackbar({
            open: true,
            message: 'Employee updated successfully!',
            severity: 'success'
          });
        }
      }
      
      handleCloseDialog();
      fetchEmployees();
    } catch (err: any) {
      console.error('Error saving employee:', err);
      setSnackbar({
        open: true,
        message: 'Failed to save employee. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleDeleteEmployee = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await deleteEmployee(id);
        setSnackbar({
          open: true,
          message: 'Employee deleted successfully!',
          severity: 'success'
        });
        fetchEmployees();
      } catch (err: any) {
        console.error('Error deleting employee:', err);
        setSnackbar({
          open: true,
          message: 'Failed to delete employee. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const handleOpenAttendanceDialog = (employee: Employee) => {
    setCurrentAttendance({
      employeeId: employee.id,
      employeeName: employee.name,
      date: new Date(),
      timeIn: null,
      timeOut: null,
      status: 'present',
      notes: ''
    });
    setOpenAttendanceDialog(true);
  };

  const handleCloseAttendanceDialog = () => {
    setOpenAttendanceDialog(false);
  };

  const handleAttendanceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentAttendance({
      ...currentAttendance,
      [name]: value
    });
  };

  const handleAttendanceStatusChange = (e: any) => {
    setCurrentAttendance({
      ...currentAttendance,
      status: e.target.value
    });
  };

  const handleSubmitAttendance = async () => {
    try {
      if (!currentAttendance.employeeId) return;
      
      // Format the attendance data for API
      let timeInStr = null;
      let timeOutStr = null;
      
      if (currentAttendance.status === 'present' || currentAttendance.status === 'late') {
        if (!currentAttendance.timeIn) {
          setSnackbar({
            open: true,
            message: 'Please enter time in for attendance.',
            severity: 'error'
          });
          return;
        }
        
        timeInStr = currentAttendance.timeIn ? format(currentAttendance.timeIn, 'HH:mm:ss') : undefined;
        timeOutStr = currentAttendance.timeOut ? format(currentAttendance.timeOut, 'HH:mm:ss') : undefined;
      }
      
      // Format the date
      const dateStr = format(currentAttendance.date, 'yyyy-MM-dd');
      
      // Prepare shift data
      const shiftData = {
        date: dateStr,
        startTime: timeInStr || undefined,
        endTime: timeOutStr || undefined,
        status: currentAttendance.status,
        notes: currentAttendance.notes,
        isRecurring: false,
        lateMinutes: currentAttendance.status === 'late' ? currentAttendance.lateMinutes : undefined
      };
      
      // Use the existing API to create a shift with attendance data
      await createEmployeeShift(currentAttendance.employeeId, shiftData);
      
      setSnackbar({
        open: true,
        message: 'Attendance recorded successfully!',
        severity: 'success'
      });
      
      handleCloseAttendanceDialog();
    } catch (err: any) {
      console.error('Error recording attendance:', err);
      setSnackbar({
        open: true,
        message: 'Failed to record attendance. Please try again.',
        severity: 'error'
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('navigation.accounting.employees')}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('add')}
        >
          {t('employees.addEmployee')}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('employees.name')}</TableCell>
                <TableCell>{t('employees.dateOfBirth')}</TableCell>
                <TableCell>{t('employees.mobileNumber')}</TableCell>
                <TableCell>{t('employees.rateType')}</TableCell>
                <TableCell align="right">{t('employees.rateAmount')}</TableCell>
                <TableCell>{t('employees.status')}</TableCell>
                <TableCell align="center">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.map((employee) => (
                <TableRow key={employee.id}>
                  <TableCell>{employee.name}</TableCell>
                  <TableCell>
                    {employee.dateOfBirth && format(new Date(employee.dateOfBirth), 'dd/MM/yyyy')}
                  </TableCell>
                  <TableCell>{employee.mobileNumber}</TableCell>
                  <TableCell>
                    {employee.rateType === 'daily' 
                      ? t('employees.rateTypes.daily') 
                      : t('employees.rateTypes.monthly')}
                  </TableCell>
                  <TableCell align="right">${employee.rateAmount.toFixed(2)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={employee.isActive ? t('common.active') : t('common.inactive')} 
                      color={employee.isActive ? 'success' : 'default'} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('common.edit')}>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleOpenDialog('edit', employee)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('employees.manageShifts')}>
                      <IconButton 
                        color="secondary"
                        onClick={() => navigate(`/dashboard/accounting/employees/${employee.id}/shifts`)}
                      >
                        <CalendarIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Record Attendance">
                      <IconButton 
                        color="info" 
                        onClick={() => handleOpenAttendanceDialog(employee)}
                      >
                        <TimeIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.delete')}>
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteEmployee(employee.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {employees.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {t('employees.noEmployees')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Employee Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? t('employees.addEmployee') : t('employees.editEmployee')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label={t('employees.name')}
              fullWidth
              required
              value={currentEmployee.name || ''}
              onChange={handleInputChange}
            />
            <Box sx={{ my: 2 }}>
              <DatePicker 
                label={t('employees.dateOfBirth')}
                value={dateOfBirth}
                onChange={(date) => setDateOfBirth(date)}
              />
            </Box>
            <TextField
              margin="dense"
              name="mobileNumber"
              label={t('employees.mobileNumber')}
              fullWidth
              required
              value={currentEmployee.mobileNumber || ''}
              onChange={handleInputChange}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>{t('employees.rateType')}</InputLabel>
              <Select
                name="rateType"
                value={currentEmployee.rateType || 'daily'}
                onChange={handleSelectChange}
              >
                <MenuItem value="daily">{t('employees.rateTypes.daily')}</MenuItem>
                <MenuItem value="monthly">{t('employees.rateTypes.monthly')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              name="rateAmount"
              label={t('employees.rateAmount')}
              fullWidth
              required
              type="number"
              value={currentEmployee.rateAmount || ''}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: '$',
              }}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>{t('employees.status')}</InputLabel>
              <Select
                name="isActive"
                value={currentEmployee.isActive === undefined ? "true" : currentEmployee.isActive ? "true" : "false"}
                onChange={handleSelectChange}
              >
                <MenuItem value="true">{t('common.active')}</MenuItem>
                <MenuItem value="false">{t('common.inactive')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              name="notes"
              label={t('employees.notes')}
              fullWidth
              multiline
              rows={3}
              value={currentEmployee.notes || ''}
              onChange={handleInputChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Attendance Dialog */}
      <Dialog open={openAttendanceDialog} onClose={handleCloseAttendanceDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Daily Attendance: {currentAttendance.employeeName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <DatePicker
              label="Date"
              value={currentAttendance.date}
              onChange={(date) => setCurrentAttendance({...currentAttendance, date: date || new Date()})}
              slotProps={{ textField: { fullWidth: true, margin: 'dense' } }}
            />
            
            <FormControl fullWidth margin="dense">
              <InputLabel>Status</InputLabel>
              <Select
                value={currentAttendance.status}
                onChange={handleAttendanceStatusChange}
                name="status"
              >
                <MenuItem value="present">Present</MenuItem>
                <MenuItem value="absent">Absent</MenuItem>
                <MenuItem value="late">Late</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
              </Select>
            </FormControl>
            
            {currentAttendance.status === 'present' && (
              <>
                <Box sx={{ mt: 2 }}>
                  <TimePicker
                    label="Time In"
                    value={currentAttendance.timeIn}
                    onChange={(time) => setCurrentAttendance({...currentAttendance, timeIn: time})}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Box>
                
                <Box sx={{ mt: 2 }}>
                  <TimePicker
                    label="Time Out"
                    value={currentAttendance.timeOut}
                    onChange={(time) => setCurrentAttendance({...currentAttendance, timeOut: time})}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Box>
              </>
            )}
            
            {currentAttendance.status === 'late' && (
              <>
                <Box sx={{ mt: 2 }}>
                  <TimePicker
                    label="Time In"
                    value={currentAttendance.timeIn}
                    onChange={(time) => setCurrentAttendance({...currentAttendance, timeIn: time})}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Box>
                
                <Box sx={{ mt: 2 }}>
                  <TimePicker
                    label="Time Out"
                    value={currentAttendance.timeOut}
                    onChange={(time) => setCurrentAttendance({...currentAttendance, timeOut: time})}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Box>
                
                <TextField
                  margin="dense"
                  name="lateMinutes"
                  label="Late by (minutes)"
                  fullWidth
                  type="number"
                  value={currentAttendance.lateMinutes || ''}
                  onChange={(e) => setCurrentAttendance({
                    ...currentAttendance, 
                    lateMinutes: parseInt(e.target.value)
                  })}
                />
              </>
            )}
            
            <TextField
              margin="dense"
              name="notes"
              label="Notes"
              fullWidth
              multiline
              rows={3}
              value={currentAttendance.notes}
              onChange={handleAttendanceInputChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAttendanceDialog}>Cancel</Button>
          <Button onClick={handleSubmitAttendance} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Employees; 